import React, { useEffect, useState } from 'react';
import { FaPen, FaEye, FaFilePdf, FaCheck, FaMoneyBillWave, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import {
  getPaieStats, getPaieContrats, getPaieContrat, createPaieContrat, updatePaieContrat,
  getPaieBulletins, getPaieBulletin, genererBulletin, genererBulletinsMasse, validerBulletin, payerBulletin, downloadBulletinsPdf, downloadBulletinPdf,
  getPaiePrimes, createPaiePrime, deletePaiePrime,
  getPaieHeuresSup, createPaieHeureSup, deletePaieHeureSup,
  getPaieAvances, createPaieAvance, annulerPaieAvance,
  getPaieConges, createPaieConge, approuverConge, refuserConge,
  getPaieAbsences, createPaieAbsence,
  getLivrePaie, getJournalPaie, getDeclarationsSociales, getHistoriqueSalarie,
  getStations, getRhEmployes
} from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import SearchableSelect from '../components/SearchableSelect';

const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];
const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';
const MOIS_LABELS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const TYPES_PRIMES = ['rendement', 'anciennete', 'transport', 'logement', 'panier', 'risque', 'responsabilite', 'exceptionnelle'];
const CHART_COLORS = ['#004A97', '#ED0000', '#FF6B35', '#78BE20', '#9c27b0', '#2196f3', '#ff9800', '#607d8b'];

const Paie: React.FC = () => {
  const { user } = useAuth();
  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');
  const [tab, setTab] = useState('dashboard');
  const [stationFilter, setStationFilter] = useState('');
  const [moisFilter, setMoisFilter] = useState(String(new Date().getMonth() + 1));
  const [anneeFilter, setAnneeFilter] = useState(String(new Date().getFullYear()));
  const [stations, setStations] = useState<any[]>([]);
  const [employes, setEmployes] = useState<any[]>([]);

  // Dashboard
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Contrats
  const [contrats, setContrats] = useState<any[]>([]);
  const [loadingContrats, setLoadingContrats] = useState(false);
  const [showContratForm, setShowContratForm] = useState(false);
  const [editContrat, setEditContrat] = useState(false);
  const [editContratId, setEditContratId] = useState<number | null>(null);
  const [contratForm, setContratForm] = useState({
    user_id: '', type_contrat: 'CDI', date_debut: '', date_fin: '', salaire_base: '',
    poste: '', categorie_professionnelle: '', is_cadre: false, nb_parts: '1'
  });
  const [savingContrat, setSavingContrat] = useState(false);

  // Bulletins
  const [bulletins, setBulletins] = useState<any[]>([]);
  const [loadingBulletins, setLoadingBulletins] = useState(false);
  const [bulletinStatut, setBulletinStatut] = useState('');
  const [selectedBulletin, setSelectedBulletin] = useState<any>(null);
  const [showBulletinDetail, setShowBulletinDetail] = useState(false);
  const [bulletinDetailLoading, setBulletinDetailLoading] = useState(false);
  const [genereLoading, setGenereLoading] = useState(false);
  const [showGenererForm, setShowGenererForm] = useState(false);
  const [genererUserId, setGenererUserId] = useState('');

  // Primes & HS
  const [primes, setPrimes] = useState<any[]>([]);
  const [loadingPrimes, setLoadingPrimes] = useState(false);
  const [primeForm, setPrimeForm] = useState({ user_id: '', type_prime: 'rendement', montant: '', mois: '', annee: '', description: '' });
  const [savingPrime, setSavingPrime] = useState(false);
  const [heuresSup, setHeuresSup] = useState<any[]>([]);
  const [loadingHS, setLoadingHS] = useState(false);
  const [hsForm, setHsForm] = useState({ user_id: '', nb_heures: '', taux_majoration: '15', date_hs: '', motif: '' });
  const [savingHS, setSavingHS] = useState(false);
  const [primesUserFilter, setPrimesUserFilter] = useState('');

  // Avances
  const [avances, setAvances] = useState<any[]>([]);
  const [loadingAvances, setLoadingAvances] = useState(false);
  const [showAvanceForm, setShowAvanceForm] = useState(false);
  const [avanceForm, setAvanceForm] = useState({ user_id: '', montant: '', motif: '', date_avance: '' });
  const [savingAvance, setSavingAvance] = useState(false);

  // Conges & Absences
  const [conges, setConges] = useState<any[]>([]);
  const [loadingConges, setLoadingConges] = useState(false);
  const [showCongeForm, setShowCongeForm] = useState(false);
  const [congeForm, setCongeForm] = useState({ user_id: '', type_conge: 'annuel', date_debut: '', date_fin: '', motif: '' });
  const [savingConge, setSavingConge] = useState(false);
  const [absences, setAbsences] = useState<any[]>([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ user_id: '', date_debut: '', date_fin: '', type_absence: 'non_justifiee', motif: '', duree_heures: '' });
  const [savingAbsence, setSavingAbsence] = useState(false);

  // Etats
  const [etatsSubTab, setEtatsSubTab] = useState('livre');
  const [livrePaie, setLivrePaie] = useState<any>(null);
  const [journalPaie, setJournalPaie] = useState<any>(null);
  const [declarations, setDeclarations] = useState<any>(null);
  const [loadingEtats, setLoadingEtats] = useState(false);

  // Historique
  const [histoUserId, setHistoUserId] = useState('');
  const [historique, setHistorique] = useState<any>(null);
  const [loadingHisto, setLoadingHisto] = useState(false);

  // Saving flags
  const [saving, setSaving] = useState(false);

  const employeOptions = employes.map((e: any) => ({
    value: String(e.id),
    label: `${e.prenom || ''} ${e.nom || ''}`.trim(),
    station_id: e.station_id ? String(e.station_id) : '',
    station_nom: e.station_nom || ''
  }));

  useEffect(() => {
    if (isGlobal) getStations().then(r => setStations(r.data)).catch(console.error);
    getRhEmployes().then(r => setEmployes(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (tab === 'dashboard') loadStats();
    if (tab === 'contrats') loadContrats();
    if (tab === 'bulletins') loadBulletins();
    if (tab === 'primes') { loadPrimes(); loadHeuresSup(); }
    if (tab === 'avances') loadAvances();
    if (tab === 'conges') { loadConges(); loadAbsences(); }
    if (tab === 'etats') loadEtats();
  }, [tab, stationFilter, moisFilter, anneeFilter]);

  // ─── Loaders ───────────────────────────────────────────────────────
  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (stationFilter) params.station_id = stationFilter;
      const res = await getPaieStats(params);
      setStats(res.data);
    } catch (err) { console.error(err); }
    setLoadingStats(false);
  };

  const loadContrats = async () => {
    setLoadingContrats(true);
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      const res = await getPaieContrats(params);
      setContrats(res.data);
    } catch (err) { console.error(err); }
    setLoadingContrats(false);
  };

  const loadBulletins = async () => {
    setLoadingBulletins(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (stationFilter) params.station_id = stationFilter;
      if (bulletinStatut) params.statut = bulletinStatut;
      const res = await getPaieBulletins(params);
      setBulletins(res.data);
    } catch (err) { console.error(err); }
    setLoadingBulletins(false);
  };

  const loadPrimes = async () => {
    setLoadingPrimes(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (primesUserFilter) params.user_id = primesUserFilter;
      const res = await getPaiePrimes(params);
      setPrimes(res.data);
    } catch (err) { console.error(err); }
    setLoadingPrimes(false);
  };

  const loadHeuresSup = async () => {
    setLoadingHS(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (primesUserFilter) params.user_id = primesUserFilter;
      const res = await getPaieHeuresSup(params);
      setHeuresSup(res.data);
    } catch (err) { console.error(err); }
    setLoadingHS(false);
  };

  const loadAvances = async () => {
    setLoadingAvances(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (stationFilter) params.station_id = stationFilter;
      const res = await getPaieAvances(params);
      setAvances(res.data);
    } catch (err) { console.error(err); }
    setLoadingAvances(false);
  };

  const loadConges = async () => {
    setLoadingConges(true);
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      const res = await getPaieConges(params);
      setConges(res.data);
    } catch (err) { console.error(err); }
    setLoadingConges(false);
  };

  const loadAbsences = async () => {
    setLoadingAbsences(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (stationFilter) params.station_id = stationFilter;
      const res = await getPaieAbsences(params);
      setAbsences(res.data);
    } catch (err) { console.error(err); }
    setLoadingAbsences(false);
  };

  const loadEtats = async () => {
    setLoadingEtats(true);
    try {
      const params: any = { mois: moisFilter, annee: anneeFilter };
      if (stationFilter) params.station_id = stationFilter;
      if (etatsSubTab === 'livre') {
        const res = await getLivrePaie(params);
        setLivrePaie(res.data);
      } else if (etatsSubTab === 'journal') {
        const res = await getJournalPaie(params);
        setJournalPaie(res.data);
      } else {
        const res = await getDeclarationsSociales(params);
        setDeclarations(res.data);
      }
    } catch (err) { console.error(err); }
    setLoadingEtats(false);
  };

  useEffect(() => {
    if (tab === 'bulletins') loadBulletins();
  }, [bulletinStatut]);

  useEffect(() => {
    if (tab === 'primes') { loadPrimes(); loadHeuresSup(); }
  }, [primesUserFilter]);

  useEffect(() => {
    if (tab === 'etats') loadEtats();
  }, [etatsSubTab]);

  // ─── Contrat CRUD ──────────────────────────────────────────────────
  const openContratCreate = () => {
    setEditContrat(false); setEditContratId(null);
    setContratForm({ user_id: '', type_contrat: 'CDI', date_debut: '', date_fin: '', salaire_base: '', poste: '', categorie_professionnelle: '', is_cadre: false, nb_parts: '1' });
    setShowContratForm(true);
  };

  const openContratEdit = async (id: number) => {
    try {
      const res = await getPaieContrat(id);
      const c = res.data;
      setEditContrat(true); setEditContratId(id);
      setContratForm({
        user_id: String(c.user_id), type_contrat: c.type_contrat, date_debut: c.date_debut || '',
        date_fin: c.date_fin || '', salaire_base: String(c.salaire_base), poste: c.poste || '',
        categorie_professionnelle: c.categorie_professionnelle || '', is_cadre: !!c.is_cadre,
        nb_parts: String(c.nb_parts || 1)
      });
      setShowContratForm(true);
    } catch (err) { console.error(err); }
  };

  const handleSaveContrat = async () => {
    if (!contratForm.user_id || !contratForm.salaire_base || !contratForm.date_debut) return alert('Employe, salaire de base et date debut sont requis');
    setSavingContrat(true);
    try {
      const data: any = {
        ...contratForm,
        user_id: Number(contratForm.user_id),
        salaire_base: Number(contratForm.salaire_base),
        is_cadre: contratForm.is_cadre ? 1 : 0,
        nb_parts: Number(contratForm.nb_parts) || 1
      };
      if (!data.date_fin) delete data.date_fin;
      if (editContrat && editContratId) {
        await updatePaieContrat(editContratId, data);
      } else {
        await createPaieContrat(data);
      }
      setShowContratForm(false);
      loadContrats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSavingContrat(false);
  };

  // ─── Bulletin actions ──────────────────────────────────────────────
  const openBulletinDetail = async (id: number) => {
    setBulletinDetailLoading(true);
    setShowBulletinDetail(true);
    try {
      const res = await getPaieBulletin(id);
      setSelectedBulletin(res.data);
    } catch (err) { console.error(err); }
    setBulletinDetailLoading(false);
  };

  const handleGenerer = async () => {
    if (!genererUserId) return alert('Selectionnez un employe');
    setGenereLoading(true);
    try {
      await genererBulletin({ user_id: Number(genererUserId), mois: Number(moisFilter), annee: Number(anneeFilter) });
      setShowGenererForm(false);
      setGenererUserId('');
      loadBulletins();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur de generation');
    }
    setGenereLoading(false);
  };

  const handleGenererMasse = async () => {
    if (!stationFilter) return alert('Selectionnez une station pour generer les bulletins en masse');
    const stationNom = stations.find((s: any) => String(s.id) === stationFilter)?.nom || '';
    if (!window.confirm(`Generer les bulletins de ${MOIS_LABELS[Number(moisFilter) - 1]} ${anneeFilter} pour la station "${stationNom}" ?`)) return;
    setGenereLoading(true);
    try {
      await genererBulletinsMasse({ station_id: Number(stationFilter), mois: Number(moisFilter), annee: Number(anneeFilter) });
      loadBulletins();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur de generation en masse');
    }
    setGenereLoading(false);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await downloadBulletinsPdf({ station_id: stationFilter, mois: moisFilter, annee: anneeFilter });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stationNom = stations.find((s: any) => String(s.id) === stationFilter)?.nom || 'Station';
      a.href = url;
      a.download = `Bulletins_${stationNom.replace(/\s+/g, '_')}_${moisFilter}_${anneeFilter}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur telechargement PDF');
    }
  };

  const handleDownloadSinglePdf = async (id: number, prenom: string, nom: string) => {
    try {
      const res = await downloadBulletinPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bulletin_${prenom}_${nom}_${moisFilter}_${anneeFilter}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur telechargement PDF');
    }
  };

  const handleValiderBulletin = async (id: number) => {
    if (!window.confirm('Valider ce bulletin ?')) return;
    try {
      await validerBulletin(id);
      loadBulletins();
      if (showBulletinDetail && selectedBulletin?.id === id) {
        const res = await getPaieBulletin(id);
        setSelectedBulletin(res.data);
      }
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handlePayerBulletin = async (id: number) => {
    if (!window.confirm('Marquer ce bulletin comme paye ?')) return;
    try {
      await payerBulletin(id);
      loadBulletins();
      if (showBulletinDetail && selectedBulletin?.id === id) {
        const res = await getPaieBulletin(id);
        setSelectedBulletin(res.data);
      }
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  // ─── Primes actions ────────────────────────────────────────────────
  const handleAddPrime = async () => {
    if (!primeForm.user_id || !primeForm.montant) return alert('Employe et montant requis');
    setSavingPrime(true);
    try {
      await createPaiePrime({
        ...primeForm,
        user_id: Number(primeForm.user_id),
        montant: Number(primeForm.montant),
        mois: Number(primeForm.mois || moisFilter),
        annee: Number(primeForm.annee || anneeFilter)
      });
      setPrimeForm({ user_id: '', type_prime: 'rendement', montant: '', mois: '', annee: '', description: '' });
      loadPrimes();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSavingPrime(false);
  };

  const handleDeletePrime = async (id: number) => {
    if (!window.confirm('Supprimer cette prime ?')) return;
    try { await deletePaiePrime(id); loadPrimes(); } catch (err) { console.error(err); }
  };

  // ─── Heures Sup actions ────────────────────────────────────────────
  const handleAddHS = async () => {
    if (!hsForm.user_id || !hsForm.nb_heures || !hsForm.taux_majoration) return alert('Employe, heures et taux requis');
    setSavingHS(true);
    try {
      await createPaieHeureSup({
        ...hsForm,
        user_id: Number(hsForm.user_id),
        nb_heures: Number(hsForm.nb_heures),
        taux_majoration: Number(hsForm.taux_majoration)
      });
      setHsForm({ user_id: '', nb_heures: '', taux_majoration: '15', date_hs: '', motif: '' });
      loadHeuresSup();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSavingHS(false);
  };

  const handleDeleteHS = async (id: number) => {
    if (!window.confirm('Supprimer ces heures supplementaires ?')) return;
    try { await deletePaieHeureSup(id); loadHeuresSup(); } catch (err) { console.error(err); }
  };

  // ─── Avances actions ───────────────────────────────────────────────
  const handleAddAvance = async () => {
    if (!avanceForm.user_id || !avanceForm.montant) return alert('Employe et montant requis');
    setSavingAvance(true);
    try {
      await createPaieAvance({
        ...avanceForm,
        user_id: Number(avanceForm.user_id),
        montant: Number(avanceForm.montant)
      });
      setShowAvanceForm(false);
      setAvanceForm({ user_id: '', montant: '', motif: '', date_avance: '' });
      loadAvances();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSavingAvance(false);
  };

  const handleAnnulerAvance = async (id: number) => {
    if (!window.confirm('Annuler cette avance ?')) return;
    try { await annulerPaieAvance(id); loadAvances(); } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  // ─── Conges actions ────────────────────────────────────────────────
  const handleAddConge = async () => {
    if (!congeForm.user_id || !congeForm.date_debut || !congeForm.date_fin) return alert('Employe, date debut et date fin requis');
    setSavingConge(true);
    try {
      await createPaieConge({
        ...congeForm,
        user_id: Number(congeForm.user_id)
      });
      setShowCongeForm(false);
      setCongeForm({ user_id: '', type_conge: 'annuel', date_debut: '', date_fin: '', motif: '' });
      loadConges();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSavingConge(false);
  };

  const handleApprouverConge = async (id: number) => {
    if (!window.confirm('Approuver ce conge ?')) return;
    try { await approuverConge(id); loadConges(); } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const handleRefuserConge = async (id: number) => {
    if (!window.confirm('Refuser ce conge ?')) return;
    try { await refuserConge(id); loadConges(); } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
  };

  // ─── Absences actions ──────────────────────────────────────────────
  const handleAddAbsence = async () => {
    if (!absenceForm.user_id || !absenceForm.date_debut) return alert('Employe et date debut requis');
    setSavingAbsence(true);
    try {
      await createPaieAbsence({
        user_id: Number(absenceForm.user_id),
        date_debut: absenceForm.date_debut,
        date_fin: absenceForm.date_fin || absenceForm.date_debut,
        type_absence: absenceForm.type_absence,
        motif: absenceForm.motif || undefined,
        duree_heures: absenceForm.duree_heures ? Number(absenceForm.duree_heures) : undefined
      });
      setShowAbsenceForm(false);
      setAbsenceForm({ user_id: '', date_debut: '', date_fin: '', type_absence: 'non_justifiee', motif: '', duree_heures: '' });
      loadAbsences();
    } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    setSavingAbsence(false);
  };

  // ─── Historique ────────────────────────────────────────────────────
  const loadHistorique = async () => {
    if (!histoUserId) return;
    setLoadingHisto(true);
    try {
      const res = await getHistoriqueSalarie(Number(histoUserId));
      setHistorique(res.data);
    } catch (err) { console.error(err); }
    setLoadingHisto(false);
  };

  useEffect(() => {
    if (tab === 'historique' && histoUserId) loadHistorique();
  }, [histoUserId]);

  // ─── Helpers ───────────────────────────────────────────────────────
  const statutBadge = (statut: string) => {
    const colors: Record<string, string> = { brouillon: '#999', valide: '#2196f3', paye: '#4caf50' };
    return <span className="badge" style={{ background: colors[statut] || '#999', color: '#fff' }}>{statut}</span>;
  };

  const getEmployeNom = (uid: number) => {
    const e = employes.find((emp: any) => emp.id === uid);
    return e ? `${e.prenom} ${e.nom}` : `#${uid}`;
  };

  const tabs = [
    { key: 'dashboard', label: 'Tableau de bord' },
    { key: 'contrats', label: 'Contrats' },
    { key: 'bulletins', label: 'Bulletins' },
    { key: 'primes', label: 'Primes & HS' },
    { key: 'avances', label: 'Avances' },
    { key: 'conges', label: 'Conges & Absences' },
    { key: 'etats', label: 'Etats' },
    { key: 'historique', label: 'Historique' },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Gestion de la Paie</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isGlobal && (
            <select value={stationFilter} onChange={e => setStationFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Toutes les stations</option>
              {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <select value={moisFilter} onChange={e => setMoisFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
            {MOIS_LABELS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={anneeFilter} onChange={e => setAnneeFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #eee', marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '3px solid #ED0000' : '3px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#ED0000' : '#666', fontSize: 14
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: TABLEAU DE BORD
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'dashboard' && (
        loadingStats ? <div className="loading">Chargement...</div> : stats && (
          <>
            <div className="stats-grid">
              <div className="stat-card" style={{ borderTop: '4px solid #004A97' }}>
                <div className="stat-value">{formatMontant(Number(stats.masse_salariale || 0))}</div>
                <div className="stat-label">MASSE SALARIALE</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #78BE20' }}>
                <div className="stat-value">{stats.nb_employes || 0}</div>
                <div className="stat-label">EMPLOYES</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #ED0000' }}>
                <div className="stat-value">{formatMontant(Number(stats.cout_moyen || 0))}</div>
                <div className="stat-label">COUT MOYEN / EMPLOYE</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #FF6B35' }}>
                <div className="stat-value">{formatMontant(Number(stats.charges_patronales || 0))}</div>
                <div className="stat-label">CHARGES PATRONALES</div>
              </div>
            </div>

            {/* Evolution chart */}
            {stats.evolution?.length > 0 && (
              <div className="card" style={{ marginTop: 20 }}>
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Evolution de la masse salariale</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.evolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periode" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v: number) => `${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                    <Bar dataKey="masse_salariale" fill="#004A97" radius={[4, 4, 0, 0]} name="Masse salariale" />
                    <Bar dataKey="charges_patronales" fill="#FF6B35" radius={[4, 4, 0, 0]} name="Charges patronales" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Repartition par station */}
            {stats.par_station?.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
                <div className="card">
                  <h3 style={{ color: '#004A97', marginBottom: 16 }}>Masse salariale par station</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={stats.par_station.map((s: any) => ({ name: s.station_nom, value: Number(s.masse_salariale) }))}
                        cx="50%" cy="50%" outerRadius={100} dataKey="value"
                        label={({ name, value }: any) => `${name}: ${(value / 1000000).toFixed(1)}M`}>
                        {stats.par_station.map((_: any, i: number) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3 style={{ color: '#004A97', marginBottom: 16 }}>Repartition des charges</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={[
                        { name: 'Salaires nets', value: Number(stats.total_nets || 0) },
                        { name: 'Retenues salariales', value: Number(stats.total_retenues || 0) },
                        { name: 'Charges patronales', value: Number(stats.charges_patronales || 0) },
                      ].filter(d => d.value > 0)}
                        cx="50%" cy="50%" outerRadius={100} dataKey="value"
                        label={({ name, value }: any) => `${name}: ${(value / 1000000).toFixed(1)}M`}>
                        <Cell fill="#78BE20" />
                        <Cell fill="#ED0000" />
                        <Cell fill="#FF6B35" />
                      </Pie>
                      <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: CONTRATS
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'contrats' && (
        <>
          <div className="filters" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={openContratCreate}>+ Nouveau Contrat</button>
          </div>

          {loadingContrats ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employe</th><th>Type</th><th>Poste</th><th>Categorie</th>
                    <th>Cadre</th><th>Parts</th><th>Date debut</th>
                    <th style={{ textAlign: 'right' }}>Salaire base</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contrats.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun contrat</td></tr>
                  ) : contrats.map((c: any) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.prenom || ''} {c.nom || ''}</td>
                      <td><span className="badge" style={{ background: c.type_contrat === 'CDI' ? '#4caf50' : '#ff9800', color: '#fff' }}>{c.type_contrat}</span></td>
                      <td>{c.poste || '-'}</td>
                      <td>{c.categorie_professionnelle || '-'}</td>
                      <td>{c.is_cadre ? 'Oui' : 'Non'}</td>
                      <td style={{ textAlign: 'center' }}>{c.nb_parts || 1}</td>
                      <td>{c.date_debut ? new Date(c.date_debut).toLocaleDateString('fr-FR') : '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(Number(c.salaire_base))}</td>
                      <td>
                        <button className="btn btn-sm" onClick={() => openContratEdit(c.id)} title="Modifier"><FaPen /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: BULLETINS
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'bulletins' && (
        <>
          <div className="filters" style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={bulletinStatut} onChange={e => setBulletinStatut(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Tous les statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="valide">Valide</option>
              <option value="paye">Paye</option>
            </select>
            <span className="badge">{bulletins.length} bulletins</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => setShowGenererForm(true)}>Generer bulletin</button>
              <button className="btn btn-success" onClick={handleGenererMasse} disabled={genereLoading}>
                {genereLoading ? 'Generation...' : 'Generer en masse'}
              </button>
              {bulletins.length > 0 && (
                <button className="btn" style={{ background: '#8B0000', color: '#fff' }} onClick={() => {
                  if (!stationFilter) return alert('Selectionnez une station pour telecharger les bulletins PDF en masse');
                  handleDownloadPdf();
                }}>
                  Telecharger PDF station
                </button>
              )}
            </div>
          </div>

          {loadingBulletins ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employe</th><th>Periode</th><th>Statut</th>
                    <th style={{ textAlign: 'right' }}>Salaire brut</th>
                    <th style={{ textAlign: 'right' }}>Retenues</th>
                    <th style={{ textAlign: 'right' }}>Avances</th>
                    <th style={{ textAlign: 'right' }}>Net a payer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bulletins.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun bulletin pour cette periode</td></tr>
                  ) : bulletins.map((b: any) => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600 }}>{b.prenom || ''} {b.nom || ''}</td>
                      <td>{MOIS_LABELS[(b.mois || 1) - 1]} {b.annee}</td>
                      <td>{statutBadge(b.statut)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMontant(Number(b.salaire_brut || 0))}</td>
                      <td style={{ textAlign: 'right', color: '#e53935' }}>{formatMontant(Number(b.total_retenues || 0))}</td>
                      <td style={{ textAlign: 'right', color: '#ff9800' }}>{formatMontant(Number(b.avances_deduites || 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#004A97' }}>{formatMontant(Number(b.salaire_net || 0))}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm" onClick={() => openBulletinDetail(b.id)} title="Detail"><FaEye /></button>
                          <button className="btn btn-sm" style={{ background: '#8B0000', color: '#fff' }} onClick={() => handleDownloadSinglePdf(b.id, b.prenom || '', b.nom || '')} title="Telecharger PDF"><FaFilePdf /></button>
                          {b.statut === 'brouillon' && (
                            <button className="btn btn-sm btn-warning" onClick={() => handleValiderBulletin(b.id)} title="Valider"><FaCheck /></button>
                          )}
                          {b.statut === 'valide' && (
                            <button className="btn btn-sm btn-success" onClick={() => handlePayerBulletin(b.id)} title="Payer"><FaMoneyBillWave /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: PRIMES & HEURES SUP
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'primes' && (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <SearchableSelect
              options={employeOptions}
              value={primesUserFilter}
              onChange={v => setPrimesUserFilter(v)}
              placeholder="Tous les employes"
              stations={stations}
              showStationFilter={isGlobal}
              style={{ minWidth: 280 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* --- Primes --- */}
            <div>
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Primes</h3>

                {/* Add prime form */}
                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                  <h4 style={{ fontSize: 13, marginBottom: 8 }}>Ajouter une prime</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Employe</label>
                      <SearchableSelect
                        options={employeOptions}
                        value={primeForm.user_id}
                        onChange={v => setPrimeForm(p => ({ ...p, user_id: v }))}
                        placeholder="-- Choisir --"
                        stations={stations}
                        showStationFilter={isGlobal}
                      />
                    </div>
                    <div className="form-group">
                      <label>Type</label>
                      <select value={primeForm.type_prime} onChange={e => setPrimeForm(p => ({ ...p, type_prime: e.target.value }))}>
                        {TYPES_PRIMES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Montant (FCFA)</label>
                      <input type="number" value={primeForm.montant} onChange={e => setPrimeForm(p => ({ ...p, montant: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input type="text" value={primeForm.description} onChange={e => setPrimeForm(p => ({ ...p, description: e.target.value }))} placeholder="Optionnel" />
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={handleAddPrime} disabled={savingPrime}>
                    {savingPrime ? 'Ajout...' : 'Ajouter'}
                  </button>
                </div>

                {loadingPrimes ? <div className="loading">Chargement...</div> : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr><th>Employe</th><th>Type</th><th style={{ textAlign: 'right' }}>Montant</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {primes.length === 0 ? (
                          <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune prime</td></tr>
                        ) : primes.map((p: any) => (
                          <tr key={p.id}>
                            <td style={{ fontWeight: 600 }}>{p.prenom || ''} {p.nom || ''}</td>
                            <td><span className="badge" style={{ background: '#004A97', color: '#fff' }}>{p.type_prime}</span></td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(Number(p.montant))}</td>
                            <td>
                              <button className="btn btn-sm btn-danger" onClick={() => handleDeletePrime(p.id)} title="Supprimer">&times;</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* --- Heures Supplementaires --- */}
            <div>
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Heures Supplementaires</h3>

                {/* Add HS form */}
                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                  <h4 style={{ fontSize: 13, marginBottom: 8 }}>Ajouter des heures sup</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Employe</label>
                      <SearchableSelect
                        options={employeOptions}
                        value={hsForm.user_id}
                        onChange={v => setHsForm(p => ({ ...p, user_id: v }))}
                        placeholder="-- Choisir --"
                        stations={stations}
                        showStationFilter={isGlobal}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input type="date" value={hsForm.date_hs} onChange={e => setHsForm(p => ({ ...p, date_hs: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nb heures</label>
                      <input type="number" value={hsForm.nb_heures} onChange={e => setHsForm(p => ({ ...p, nb_heures: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="form-group">
                      <label>Taux majoration (%)</label>
                      <input type="number" value={hsForm.taux_majoration} onChange={e => setHsForm(p => ({ ...p, taux_majoration: e.target.value }))} placeholder="15" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Motif</label>
                    <input type="text" value={hsForm.motif} onChange={e => setHsForm(p => ({ ...p, motif: e.target.value }))} placeholder="Optionnel" />
                  </div>
                  <button className="btn btn-primary" onClick={handleAddHS} disabled={savingHS}>
                    {savingHS ? 'Ajout...' : 'Ajouter'}
                  </button>
                </div>

                {loadingHS ? <div className="loading">Chargement...</div> : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr><th>Employe</th><th>Date</th><th style={{ textAlign: 'right' }}>Heures</th><th style={{ textAlign: 'right' }}>Taux</th><th style={{ textAlign: 'right' }}>Montant</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {heuresSup.length === 0 ? (
                          <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune heure sup</td></tr>
                        ) : heuresSup.map((h: any) => (
                          <tr key={h.id}>
                            <td style={{ fontWeight: 600 }}>{h.prenom || ''} {h.nom || ''}</td>
                            <td>{h.date_hs ? new Date(h.date_hs).toLocaleDateString('fr-FR') : '-'}</td>
                            <td style={{ textAlign: 'right' }}>{h.nb_heures}h</td>
                            <td style={{ textAlign: 'right' }}>{Number(h.taux_majoration) || 0}%</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(Number(h.montant))}</td>
                            <td>
                              <button className="btn btn-sm btn-danger" onClick={() => handleDeleteHS(h.id)} title="Supprimer">&times;</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: AVANCES
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'avances' && (
        <>
          <div className="filters" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => setShowAvanceForm(true)}>+ Nouvelle Avance</button>
          </div>

          {loadingAvances ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employe</th><th>Date</th><th style={{ textAlign: 'right' }}>Montant</th>
                    <th>Motif</th><th>Statut</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {avances.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune avance</td></tr>
                  ) : avances.map((a: any) => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 600 }}>{a.prenom || ''} {a.nom || ''}</td>
                      <td>{a.date_avance ? new Date(a.date_avance).toLocaleDateString('fr-FR') : '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(Number(a.montant))}</td>
                      <td>{a.motif || '-'}</td>
                      <td>
                        <span className="badge" style={{
                          background: (a.statut === 'active' || a.statut === 'en_cours') ? '#ff9800' : a.statut === 'remboursee' || a.statut === 'deduite' ? '#4caf50' : a.statut === 'annulee' ? '#f44336' : '#999',
                          color: '#fff'
                        }}>{a.statut}</span>
                      </td>
                      <td>
                        {(a.statut === 'active' || a.statut === 'en_cours') && (
                          <button className="btn btn-sm btn-danger" onClick={() => handleAnnulerAvance(a.id)} title="Annuler">Annuler</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: CONGES & ABSENCES
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'conges' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* --- Conges --- */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ color: '#004A97' }}>Conges</h3>
                <button className="btn btn-primary" onClick={() => setShowCongeForm(true)}>+ Nouveau Conge</button>
              </div>

              {loadingConges ? <div className="loading">Chargement...</div> : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr><th>Employe</th><th>Type</th><th>Debut</th><th>Fin</th><th>Statut</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {conges.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucun conge</td></tr>
                      ) : conges.map((c: any) => (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 600 }}>{c.prenom || ''} {c.nom || ''}</td>
                          <td>{c.type_conge}</td>
                          <td>{c.date_debut ? new Date(c.date_debut).toLocaleDateString('fr-FR') : '-'}</td>
                          <td>{c.date_fin ? new Date(c.date_fin).toLocaleDateString('fr-FR') : '-'}</td>
                          <td>
                            <span className="badge" style={{
                              background: c.statut === 'approuve' ? '#4caf50' : c.statut === 'refuse' ? '#e53935' : '#ff9800',
                              color: '#fff'
                            }}>{c.statut}</span>
                          </td>
                          <td>
                            {c.statut === 'en_attente' && (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button className="btn btn-sm btn-success" onClick={() => handleApprouverConge(c.id)} title="Approuver"><FaCheck /></button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleRefuserConge(c.id)} title="Refuser"><FaTimes /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* --- Absences --- */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ color: '#004A97' }}>Absences</h3>
                <button className="btn btn-primary" onClick={() => setShowAbsenceForm(true)}>+ Nouvelle Absence</button>
              </div>

              {loadingAbsences ? <div className="loading">Chargement...</div> : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr><th>Employe</th><th>Date</th><th>Type</th><th>Duree</th><th>Motif</th></tr>
                    </thead>
                    <tbody>
                      {absences.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune absence</td></tr>
                      ) : absences.map((a: any) => (
                        <tr key={a.id}>
                          <td style={{ fontWeight: 600 }}>{a.prenom || ''} {a.nom || ''}</td>
                          <td>{a.date_absence ? new Date(a.date_absence).toLocaleDateString('fr-FR') : '-'}</td>
                          <td>
                            <span className="badge" style={{
                              background: a.type_absence === 'justifiee' ? '#4caf50' : '#e53935', color: '#fff'
                            }}>{a.type_absence}</span>
                          </td>
                          <td>{a.duree_heures ? `${a.duree_heures}h` : 'Journee'}</td>
                          <td>{a.motif || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: ETATS
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'etats' && (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {[
              { key: 'livre', label: 'Livre de paie' },
              { key: 'journal', label: 'Journal de paie' },
              { key: 'declarations', label: 'Declarations sociales' },
            ].map(st => (
              <button key={st.key} onClick={() => setEtatsSubTab(st.key)}
                style={{
                  padding: '8px 16px', border: '1px solid #ddd',
                  borderRadius: 6, cursor: 'pointer',
                  background: etatsSubTab === st.key ? '#004A97' : '#fff',
                  color: etatsSubTab === st.key ? '#fff' : '#333',
                  fontWeight: etatsSubTab === st.key ? 700 : 400
                }}>
                {st.label}
              </button>
            ))}
          </div>

          {loadingEtats ? <div className="loading">Chargement...</div> : (
            <>
              {/* Livre de paie */}
              {etatsSubTab === 'livre' && livrePaie && (
                <div className="card" style={{ overflow: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ color: '#004A97' }}>Livre de Paie - {MOIS_LABELS[Number(moisFilter) - 1]} {anneeFilter}</h3>
                    <button className="btn btn-sm" onClick={() => window.print()}>Imprimer</button>
                  </div>
                  <div className="table-responsive">
                    <table className="table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Nom</th><th style={{ textAlign: 'right' }}>Salaire Base</th>
                          <th style={{ textAlign: 'right' }}>Brut</th><th style={{ textAlign: 'right' }}>IPRES</th>
                          <th style={{ textAlign: 'right' }}>IR</th><th style={{ textAlign: 'right' }}>TRIMF</th>
                          <th style={{ textAlign: 'right' }}>CSS</th><th style={{ textAlign: 'right' }}>Retenues</th>
                          <th style={{ textAlign: 'right' }}>Avances</th><th style={{ textAlign: 'right', fontWeight: 700 }}>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(livrePaie.lignes || []).length === 0 ? (
                          <tr><td colSpan={10} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune donnee</td></tr>
                        ) : (livrePaie.lignes || []).map((l: any, i: number) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{l.nom}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.salaire_base || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.salaire_brut || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.ipres_general_employe || 0) + Number(l.ipres_cadre_employe || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.ir || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.trimf || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(l.css_employe || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#e53935' }}>{formatMontant(Number(l.total_retenues || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#ff9800' }}>{formatMontant(Number(l.avances_deduites || 0))}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#004A97' }}>{formatMontant(Number(l.salaire_net || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                      {livrePaie.totaux && (
                        <tfoot>
                          <tr style={{ fontWeight: 700, borderTop: '2px solid #333', background: '#f0f4f8' }}>
                            <td>TOTAUX</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.salaire_base || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.salaire_brut || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.ipres_general_employe || 0) + Number(livrePaie.totaux.ipres_cadre_employe || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.ir || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.trimf || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(livrePaie.totaux.css_employe || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#e53935' }}>{formatMontant(Number(livrePaie.totaux.total_retenues || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#ff9800' }}>{formatMontant(Number(livrePaie.totaux.avances_deduites || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#004A97' }}>{formatMontant(Number(livrePaie.totaux.salaire_net || 0))}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {/* Journal de paie */}
              {etatsSubTab === 'journal' && journalPaie && (
                <div className="card" style={{ overflow: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ color: '#004A97' }}>Journal de Paie - {MOIS_LABELS[Number(moisFilter) - 1]} {anneeFilter}</h3>
                    <button className="btn btn-sm" onClick={() => window.print()}>Imprimer</button>
                  </div>
                  <div className="table-responsive">
                    <table className="table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Rubrique</th><th>Code</th>
                          <th style={{ textAlign: 'right' }}>Base</th>
                          <th style={{ textAlign: 'right' }}>Taux (%)</th>
                          <th style={{ textAlign: 'right' }}>Part salariale</th>
                          <th style={{ textAlign: 'right' }}>Part patronale</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(journalPaie.rubriques || []).length === 0 ? (
                          <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune donnee</td></tr>
                        ) : (journalPaie.rubriques || []).map((r: any, i: number) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{r.libelle}</td>
                            <td><code style={{ fontSize: 11 }}>{r.code}</code></td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(r.base || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{r.taux ? `${r.taux}%` : '-'}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(r.part_salariale || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(r.part_patronale || 0))}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(Number(r.total || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                      {journalPaie.totaux && (
                        <tfoot>
                          <tr style={{ fontWeight: 700, borderTop: '2px solid #333', background: '#f0f4f8' }}>
                            <td colSpan={4}>TOTAUX</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(journalPaie.totaux.part_salariale || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(journalPaie.totaux.part_patronale || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(journalPaie.totaux.total || 0))}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {/* Declarations sociales */}
              {etatsSubTab === 'declarations' && declarations && (
                <div className="card" style={{ overflow: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ color: '#004A97' }}>Declarations Sociales - {MOIS_LABELS[Number(moisFilter) - 1]} {anneeFilter}</h3>
                    <button className="btn btn-sm" onClick={() => window.print()}>Imprimer</button>
                  </div>
                  <div className="table-responsive">
                    <table className="table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Employe</th>
                          <th style={{ textAlign: 'right' }}>Base CSS</th><th style={{ textAlign: 'right' }}>CSS Empl.</th>
                          <th style={{ textAlign: 'right' }}>Base IPRES</th><th style={{ textAlign: 'right' }}>IPRES Total</th>
                          <th style={{ textAlign: 'right' }}>Contrib. Solidarite</th><th style={{ textAlign: 'right' }}>IR + TRIMF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(declarations.lignes || []).length === 0 ? (
                          <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#999' }}>Aucune donnee</td></tr>
                        ) : (declarations.lignes || []).map((d: any, i: number) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{d.prenom} {d.nom}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Math.min(Number(d.salaire_brut || 0), 63000))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(d.cnss_employeur || 0) + Number(d.accident_travail || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Math.min(Number(d.salaire_brut || 0), 432000))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(d.ipres_general_employe || 0) + Number(d.ipres_general_employeur || 0) + Number(d.ipres_cadre_employe || 0) + Number(d.ipres_cadre_employeur || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(d.css_employe || 0) + Number(d.css_employeur || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(d.ir || 0) + Number(d.trimf || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                      {declarations.totaux && (
                        <tfoot>
                          <tr style={{ fontWeight: 700, borderTop: '2px solid #333', background: '#f0f4f8' }}>
                            <td>TOTAUX</td>
                            <td style={{ textAlign: 'right' }}>-</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(declarations.totaux.cnss_employeur || 0) + Number(declarations.totaux.accident_travail || 0))}</td>
                            <td style={{ textAlign: 'right' }}>-</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(declarations.totaux.ipres_general_employe || 0) + Number(declarations.totaux.ipres_general_employeur || 0) + Number(declarations.totaux.ipres_cadre_employe || 0) + Number(declarations.totaux.ipres_cadre_employeur || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(declarations.totaux.css_employe || 0) + Number(declarations.totaux.css_employeur || 0))}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(declarations.totaux.ir || 0) + Number(declarations.totaux.trimf || 0))}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TAB: HISTORIQUE
          ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'historique' && (
        <>
          <div className="filters" style={{ marginBottom: 20 }}>
            <div className="form-group" style={{ maxWidth: 400 }}>
              <label>Selectionner un employe</label>
              <SearchableSelect
                options={employeOptions}
                value={histoUserId}
                onChange={v => setHistoUserId(v)}
                placeholder="-- Choisir un employe --"
                stations={stations}
                showStationFilter={isGlobal}
              />
            </div>
          </div>

          {loadingHisto ? <div className="loading">Chargement...</div> : historique && (
            <>
              {/* Recap */}
              <div className="stats-grid" style={{ marginBottom: 20 }}>
                <div className="stat-card" style={{ borderTop: '4px solid #004A97' }}>
                  <div className="stat-value">{historique.nb_bulletins || 0}</div>
                  <div className="stat-label">BULLETINS EMIS</div>
                </div>
                <div className="stat-card" style={{ borderTop: '4px solid #78BE20' }}>
                  <div className="stat-value">{formatMontant(Number(historique.salaire_actuel || 0))}</div>
                  <div className="stat-label">SALAIRE ACTUEL</div>
                </div>
                <div className="stat-card" style={{ borderTop: '4px solid #ED0000' }}>
                  <div className="stat-value">{formatMontant(Number(historique.total_percu || 0))}</div>
                  <div className="stat-label">TOTAL PERCU</div>
                </div>
                <div className="stat-card" style={{ borderTop: '4px solid #FF6B35' }}>
                  <div className="stat-value">{formatMontant(Number(historique.moyenne_nette || 0))}</div>
                  <div className="stat-label">MOYENNE NETTE</div>
                </div>
              </div>

              {/* Evolution chart */}
              {historique.evolution?.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ color: '#004A97', marginBottom: 16 }}>Evolution du salaire net</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={historique.evolution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periode" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v: any) => formatMontant(Number(v))} />
                      <Line type="monotone" dataKey="salaire_net" stroke="#004A97" strokeWidth={2} dot={{ fill: '#004A97' }} name="Net" />
                      <Line type="monotone" dataKey="salaire_brut" stroke="#78BE20" strokeWidth={2} dot={{ fill: '#78BE20' }} name="Brut" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Bulletins history table */}
              {historique.bulletins?.length > 0 && (
                <div className="card">
                  <h3 style={{ color: '#004A97', marginBottom: 16 }}>Historique des bulletins</h3>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Periode</th><th>Statut</th>
                          <th style={{ textAlign: 'right' }}>Brut</th>
                          <th style={{ textAlign: 'right' }}>Retenues</th>
                          <th style={{ textAlign: 'right' }}>Avances</th>
                          <th style={{ textAlign: 'right' }}>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historique.bulletins.map((b: any) => (
                          <tr key={b.id}>
                            <td style={{ fontWeight: 600 }}>{MOIS_LABELS[(b.mois || 1) - 1]} {b.annee}</td>
                            <td>{statutBadge(b.statut)}</td>
                            <td style={{ textAlign: 'right' }}>{formatMontant(Number(b.salaire_brut || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#e53935' }}>{formatMontant(Number(b.total_retenues || 0))}</td>
                            <td style={{ textAlign: 'right', color: '#ff9800' }}>{formatMontant(Number(b.avances_deduites || 0))}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#004A97' }}>{formatMontant(Number(b.salaire_net || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {!histoUserId && !loadingHisto && (
            <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
              Selectionnez un employe pour voir son historique de paie
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: CONTRAT FORM
          ═══════════════════════════════════════════════════════════════════ */}
      {showContratForm && (
        <div className="modal-overlay" onClick={() => setShowContratForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editContrat ? 'Modifier le contrat' : 'Nouveau contrat'}</h3>
              <button className="modal-close" onClick={() => setShowContratForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Employe *</label>
                  <SearchableSelect
                    options={employeOptions}
                    value={contratForm.user_id}
                    onChange={v => setContratForm(p => ({ ...p, user_id: v }))}
                    placeholder="-- Choisir --"
                    stations={stations}
                    showStationFilter={isGlobal}
                  />
                </div>
                <div className="form-group">
                  <label>Type de contrat *</label>
                  <select value={contratForm.type_contrat} onChange={e => setContratForm(p => ({ ...p, type_contrat: e.target.value }))}>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Stage">Stage</option>
                    <option value="Interim">Interim</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date debut *</label>
                  <input type="date" value={contratForm.date_debut} onChange={e => setContratForm(p => ({ ...p, date_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Date fin</label>
                  <input type="date" value={contratForm.date_fin} onChange={e => setContratForm(p => ({ ...p, date_fin: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Salaire de base (FCFA) *</label>
                  <input type="number" value={contratForm.salaire_base} onChange={e => setContratForm(p => ({ ...p, salaire_base: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Poste</label>
                  <input type="text" value={contratForm.poste} onChange={e => setContratForm(p => ({ ...p, poste: e.target.value }))} placeholder="Ex: Pompiste principal" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categorie professionnelle</label>
                  <input type="text" value={contratForm.categorie_professionnelle} onChange={e => setContratForm(p => ({ ...p, categorie_professionnelle: e.target.value }))} placeholder="Ex: Cat. 3" />
                </div>
                <div className="form-group">
                  <label>Nombre de parts (quotient familial)</label>
                  <select value={contratForm.nb_parts} onChange={e => setContratForm(p => ({ ...p, nb_parts: e.target.value }))}>
                    <option value="1">1 - Celibataire sans enfant</option>
                    <option value="1.5">1.5 - Celibataire + 1 enfant</option>
                    <option value="2">2 - Marie sans enfant / Celib. + 2 enfants</option>
                    <option value="2.5">2.5 - Marie + 1 enfant / Celib. + 3 enfants</option>
                    <option value="3">3 - Marie + 2 enfants</option>
                    <option value="3.5">3.5 - Marie + 3 enfants</option>
                    <option value="4">4 - Marie + 4 enfants</option>
                    <option value="4.5">4.5 - Marie + 5 enfants</option>
                    <option value="5">5 - Maximum (5 parts)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input type="checkbox" checked={contratForm.is_cadre} onChange={e => setContratForm(p => ({ ...p, is_cadre: e.target.checked }))} />
                    Cadre (cotisation IPRES Cadre)
                  </label>
                </div>
                <div className="form-group" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowContratForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSaveContrat} disabled={savingContrat}>
                {savingContrat ? 'Enregistrement...' : (editContrat ? 'Enregistrer' : 'Creer le contrat')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: BULLETIN DETAIL
          ═══════════════════════════════════════════════════════════════════ */}
      {showBulletinDetail && (
        <div className="modal-overlay" onClick={() => setShowBulletinDetail(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulletin de Paie</h3>
              <button className="modal-close" onClick={() => setShowBulletinDetail(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {bulletinDetailLoading ? <div className="loading">Chargement...</div> : selectedBulletin && (
                <div style={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {/* Header */}
                  <div style={{ textAlign: 'center', borderBottom: '2px solid #004A97', paddingBottom: 12, marginBottom: 16 }}>
                    <h2 style={{ color: '#004A97', margin: 0 }}>BULLETIN DE PAIE</h2>
                    <p style={{ margin: '4px 0', color: '#666' }}>
                      Periode : {MOIS_LABELS[(selectedBulletin.mois || 1) - 1]} {selectedBulletin.annee}
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <strong>Employe :</strong> {selectedBulletin.prenom || ''} {selectedBulletin.nom || ''}
                    </div>
                    <div>
                      <strong>Station :</strong> {selectedBulletin.station_nom || '-'}
                    </div>
                    <div>
                      <strong>Poste :</strong> {selectedBulletin.poste || '-'}
                    </div>
                    <div>
                      <strong>Statut :</strong> {statutBadge(selectedBulletin.statut)}
                    </div>
                  </div>

                  {/* Section: Salaire Brut */}
                  <div style={{ background: '#f0f4f8', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <h4 style={{ color: '#004A97', margin: '0 0 8px', borderBottom: '1px solid #ccc', paddingBottom: 4 }}>Salaire Brut</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr><td>Salaire de base</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.salaire_base || 0))}</td></tr>
                        <tr><td>Heures supplementaires</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.heures_sup_montant || 0))}</td></tr>
                        <tr><td>Primes</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.primes_montant || 0))}</td></tr>
                        <tr><td>Conges payes</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.conges_payes_montant || 0))}</td></tr>
                        <tr><td>Indemnites</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.indemnites || 0))}</td></tr>
                        <tr style={{ fontWeight: 700, borderTop: '2px solid #004A97' }}>
                          <td>TOTAL BRUT</td>
                          <td style={{ textAlign: 'right', color: '#004A97', fontSize: 15 }}>{formatMontant(Number(selectedBulletin.salaire_brut || 0))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Section: Retenues Salariales */}
                  <div style={{ background: '#fff5f5', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <h4 style={{ color: '#e53935', margin: '0 0 8px', borderBottom: '1px solid #ecc', paddingBottom: 4 }}>Retenues Salariales</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr><td>IPRES Regime General (5.6%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.ipres_general_employe || 0))}</td></tr>
                        {Number(selectedBulletin.ipres_cadre_employe || 0) > 0 && (
                          <tr><td>IPRES Regime Cadre CRC (2.4%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.ipres_cadre_employe || 0))}</td></tr>
                        )}
                        <tr><td>Impot sur le Revenu (IR)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.ir || 0))}</td></tr>
                        <tr><td>TRIMF</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.trimf || 0))}</td></tr>
                        <tr><td>CSS Contrib. Solidarite (1%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.css_employe || 0))}</td></tr>
                        {Number(selectedBulletin.autres_retenues || 0) > 0 && (
                          <tr><td>Autres retenues</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.autres_retenues || 0))}</td></tr>
                        )}
                        <tr style={{ fontWeight: 700, borderTop: '2px solid #e53935' }}>
                          <td>TOTAL RETENUES</td>
                          <td style={{ textAlign: 'right', color: '#e53935', fontSize: 15 }}>{formatMontant(Number(selectedBulletin.total_retenues || 0))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Section: Avances */}
                  {Number(selectedBulletin.avances_deduites || 0) > 0 && (
                    <div style={{ background: '#fff8e1', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                      <h4 style={{ color: '#ff9800', margin: '0 0 8px', borderBottom: '1px solid #eed', paddingBottom: 4 }}>Avances sur salaire</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr style={{ fontWeight: 700 }}>
                            <td>Avances deduites</td>
                            <td style={{ textAlign: 'right', color: '#ff9800' }}>{formatMontant(Number(selectedBulletin.avances_deduites || 0))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Section: Salaire Net */}
                  <div style={{ background: '#e8f5e9', padding: 16, borderRadius: 8, marginBottom: 12, border: '2px solid #4caf50' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 700, fontSize: 16 }}>SALAIRE NET A PAYER</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 20, color: '#2e7d32' }}>
                            {formatMontant(Number(selectedBulletin.salaire_net || 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Section: Charges Patronales */}
                  <div style={{ background: '#fff3e0', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <h4 style={{ color: '#FF6B35', margin: '0 0 8px', borderBottom: '1px solid #eed', paddingBottom: 4 }}>Charges Patronales</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr><td>CSS Prest. Familiales (7%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.cnss_employeur || 0))}</td></tr>
                        <tr><td>CSS Accident du travail (1%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.accident_travail || 0))}</td></tr>
                        <tr><td>IPRES General Employeur (8.4%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.ipres_general_employeur || 0))}</td></tr>
                        {Number(selectedBulletin.ipres_cadre_employeur || 0) > 0 && (
                          <tr><td>IPRES Cadre Employeur CRC (3.6%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.ipres_cadre_employeur || 0))}</td></tr>
                        )}
                        <tr><td>CSS Contrib. Solidarite Empl. (3%)</td><td style={{ textAlign: 'right' }}>{formatMontant(Number(selectedBulletin.css_employeur || 0))}</td></tr>
                        <tr style={{ fontWeight: 700, borderTop: '2px solid #FF6B35' }}>
                          <td>TOTAL CHARGES PATRONALES</td>
                          <td style={{ textAlign: 'right', color: '#FF6B35', fontSize: 15 }}>{formatMontant(Number(selectedBulletin.total_charges_patronales || 0))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Section: Cout Total Employeur */}
                  <div style={{ background: '#e3f2fd', padding: 16, borderRadius: 8, border: '2px solid #004A97' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 700, fontSize: 16 }}>COUT TOTAL EMPLOYEUR</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 20, color: '#004A97' }}>
                            {formatMontant(Number(selectedBulletin.cout_total || 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedBulletin && (
                <>
                  {selectedBulletin.statut === 'brouillon' && (
                    <button className="btn btn-warning" onClick={() => handleValiderBulletin(selectedBulletin.id)}>Valider</button>
                  )}
                  {selectedBulletin.statut === 'valide' && (
                    <button className="btn btn-success" onClick={() => handlePayerBulletin(selectedBulletin.id)}>Marquer comme paye</button>
                  )}
                </>
              )}
              <button className="btn" onClick={() => setShowBulletinDetail(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: GENERER BULLETIN
          ═══════════════════════════════════════════════════════════════════ */}
      {showGenererForm && (
        <div className="modal-overlay" onClick={() => setShowGenererForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generer un bulletin de paie</h3>
              <button className="modal-close" onClick={() => setShowGenererForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Employe *</label>
                <SearchableSelect
                  options={employeOptions}
                  value={genererUserId}
                  onChange={v => setGenererUserId(v)}
                  placeholder="-- Choisir --"
                  stations={stations}
                  showStationFilter={isGlobal}
                />
              </div>
              <p style={{ color: '#666', fontSize: 13 }}>
                Periode : {MOIS_LABELS[Number(moisFilter) - 1]} {anneeFilter}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowGenererForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleGenerer} disabled={genereLoading}>
                {genereLoading ? 'Generation...' : 'Generer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: AVANCE FORM
          ═══════════════════════════════════════════════════════════════════ */}
      {showAvanceForm && (
        <div className="modal-overlay" onClick={() => setShowAvanceForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle avance sur salaire</h3>
              <button className="modal-close" onClick={() => setShowAvanceForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Employe *</label>
                  <SearchableSelect
                    options={employeOptions}
                    value={avanceForm.user_id}
                    onChange={v => setAvanceForm(p => ({ ...p, user_id: v }))}
                    placeholder="-- Choisir --"
                    stations={stations}
                    showStationFilter={isGlobal}
                  />
                </div>
                <div className="form-group">
                  <label>Montant (FCFA) *</label>
                  <input type="number" value={avanceForm.montant} onChange={e => setAvanceForm(p => ({ ...p, montant: e.target.value }))} placeholder="0" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={avanceForm.date_avance} onChange={e => setAvanceForm(p => ({ ...p, date_avance: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Motif</label>
                  <input type="text" value={avanceForm.motif} onChange={e => setAvanceForm(p => ({ ...p, motif: e.target.value }))} placeholder="Optionnel" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowAvanceForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleAddAvance} disabled={savingAvance}>
                {savingAvance ? 'Enregistrement...' : 'Creer l\'avance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: CONGE FORM
          ═══════════════════════════════════════════════════════════════════ */}
      {showCongeForm && (
        <div className="modal-overlay" onClick={() => setShowCongeForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouveau conge</h3>
              <button className="modal-close" onClick={() => setShowCongeForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Employe *</label>
                  <SearchableSelect
                    options={employeOptions}
                    value={congeForm.user_id}
                    onChange={v => setCongeForm(p => ({ ...p, user_id: v }))}
                    placeholder="-- Choisir --"
                    stations={stations}
                    showStationFilter={isGlobal}
                  />
                </div>
                <div className="form-group">
                  <label>Type de conge</label>
                  <select value={congeForm.type_conge} onChange={e => setCongeForm(p => ({ ...p, type_conge: e.target.value }))}>
                    <option value="annuel">Annuel</option>
                    <option value="maladie">Maladie</option>
                    <option value="maternite">Maternite</option>
                    <option value="paternite">Paternite</option>
                    <option value="special">Special</option>
                    <option value="sans_solde">Sans solde</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date debut *</label>
                  <input type="date" value={congeForm.date_debut} onChange={e => setCongeForm(p => ({ ...p, date_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Date fin *</label>
                  <input type="date" value={congeForm.date_fin} onChange={e => setCongeForm(p => ({ ...p, date_fin: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Motif</label>
                <input type="text" value={congeForm.motif} onChange={e => setCongeForm(p => ({ ...p, motif: e.target.value }))} placeholder="Optionnel" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowCongeForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleAddConge} disabled={savingConge}>
                {savingConge ? 'Enregistrement...' : 'Creer le conge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL: ABSENCE FORM
          ═══════════════════════════════════════════════════════════════════ */}
      {showAbsenceForm && (
        <div className="modal-overlay" onClick={() => setShowAbsenceForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle absence</h3>
              <button className="modal-close" onClick={() => setShowAbsenceForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Employe *</label>
                  <SearchableSelect
                    options={employeOptions}
                    value={absenceForm.user_id}
                    onChange={v => setAbsenceForm(p => ({ ...p, user_id: v }))}
                    placeholder="-- Choisir --"
                    stations={stations}
                    showStationFilter={isGlobal}
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={absenceForm.type_absence} onChange={e => setAbsenceForm(p => ({ ...p, type_absence: e.target.value }))}>
                    <option value="non_justifiee">Non justifiee</option>
                    <option value="retard">Retard</option>
                    <option value="maladie">Maladie</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date debut *</label>
                  <input type="date" value={absenceForm.date_debut} onChange={e => setAbsenceForm(p => ({ ...p, date_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Date fin</label>
                  <input type="date" value={absenceForm.date_fin} onChange={e => setAbsenceForm(p => ({ ...p, date_fin: e.target.value }))} min={absenceForm.date_debut || undefined} />
                </div>
              </div>
              {absenceForm.date_debut && absenceForm.date_fin && absenceForm.date_fin > absenceForm.date_debut && (
                <div style={{ background: '#e8f4fd', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#004A97' }}>
                  {(() => {
                    const d1 = new Date(absenceForm.date_debut);
                    const d2 = new Date(absenceForm.date_fin);
                    const jours = Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1;
                    return `${jours} jour${jours > 1 ? 's' : ''} = ${jours * 8}h (8h/jour)`;
                  })()}
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Duree (heures) — par jour</label>
                  <input type="number" value={absenceForm.duree_heures} onChange={e => setAbsenceForm(p => ({ ...p, duree_heures: e.target.value }))} placeholder="8h par defaut" />
                </div>
                <div className="form-group">
                  <label>Motif</label>
                  <input type="text" value={absenceForm.motif} onChange={e => setAbsenceForm(p => ({ ...p, motif: e.target.value }))} placeholder="Optionnel" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowAbsenceForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleAddAbsence} disabled={savingAbsence}>
                {savingAbsence ? 'Enregistrement...' : 'Enregistrer l\'absence'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Paie;
